/**
 * Detect stale Weekly Review sources and queue Integrations "sync all portals".
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { bulkTriggerPortalSync, useToastStore } from '@tracekit/core';
import type { WeeklyOpportunitySearchEntity } from '../utils/weeklyGlobalOpportunities';
import {
  assessWeeklyReviewStaleness,
  canTriggerStalenessResync,
  formatStaleSourceLabels,
  markStalenessResyncTriggered,
  type SourceStalenessStats,
  type WeeklyReviewStalenessAssessment,
} from '../utils/weeklyReviewStaleness';

export type WeeklyReviewStalenessResyncStatus =
  | 'idle'
  | 'stale'
  | 'queued'
  | 'cooldown'
  | 'error';

export interface UseWeeklyReviewStalenessResyncOptions {
  projectId: string | undefined;
  entities: WeeklyOpportunitySearchEntity[];
  enabled?: boolean;
  now?: Date;
}

export interface UseWeeklyReviewStalenessResyncResult {
  assessment: WeeklyReviewStalenessAssessment;
  staleSources: SourceStalenessStats[];
  status: WeeklyReviewStalenessResyncStatus;
  bannerMessage: string | null;
  error: string | null;
}

export function useWeeklyReviewStalenessResync({
  projectId,
  entities,
  enabled = true,
  now,
}: UseWeeklyReviewStalenessResyncOptions): UseWeeklyReviewStalenessResyncResult {
  const queryClient = useQueryClient();
  const addToast = useToastStore((state) => state.addToast);
  const [status, setStatus] = useState<WeeklyReviewStalenessResyncStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const triggerAttemptedRef = useRef(false);

  const assessment = useMemo(
    () => assessWeeklyReviewStaleness(entities, now ?? new Date()),
    [entities, now]
  );

  useEffect(() => {
    triggerAttemptedRef.current = false;
    setStatus('idle');
    setError(null);
  }, [projectId]);

  useEffect(() => {
    if (!enabled || !projectId || entities.length === 0) return;
    if (!assessment.isAnyStale) {
      setStatus('idle');
      return;
    }

    const clock = now ?? new Date();
    if (!canTriggerStalenessResync(projectId, clock)) {
      setStatus('cooldown');
      return;
    }

    if (triggerAttemptedRef.current) return;
    triggerAttemptedRef.current = true;
    setStatus('stale');

    let cancelled = false;

    void (async () => {
      try {
        markStalenessResyncTriggered(projectId, clock);
        const result = await bulkTriggerPortalSync(projectId);
        if (cancelled) return;

        setStatus('queued');
        setError(null);
        addToast({
          type: 'info',
          title: 'Portal sync queued',
          message:
            result.message ||
            `Queued ${result.triggered} portal sync job(s). Check the Jobs page for progress.`,
          duration: 8000,
        });
        void queryClient.invalidateQueries({ queryKey: ['jobs', projectId] });
        void queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setStatus('error');
        setError(message);
        addToast({
          type: 'warning',
          title: 'Could not queue portal sync',
          message,
          duration: 8000,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    addToast,
    assessment.isAnyStale,
    enabled,
    entities.length,
    now,
    projectId,
    queryClient,
  ]);

  const bannerMessage = useMemo(() => {
    if (!assessment.isAnyStale) return null;
    const labels = formatStaleSourceLabels(assessment.staleSources);
    if (status === 'queued') {
      return `Some sources looked outdated (${labels}). Portal sync jobs were queued — check the Jobs page for progress.`;
    }
    if (status === 'cooldown') {
      return `Some sources look outdated (${labels}). A refresh was already queued recently; check the Jobs page or try again later.`;
    }
    if (status === 'error') {
      return `Some sources look outdated (${labels}), but the automatic refresh failed. Open Integrations to sync manually.`;
    }
    return `Some sources look stale (${labels}) — refreshing portal data in the background…`;
  }, [assessment.isAnyStale, assessment.staleSources, status]);

  return {
    assessment,
    staleSources: assessment.staleSources,
    status,
    bannerMessage,
    error,
  };
}
